import { Tabs, Button, Spin, Form } from "antd"
import { useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { notifications } from "notifications"
import type { MonitoringConfigType } from "domains/managingJson/store/types"
import { MonitoringFields } from "./common/MonitoringFields"

const { TabPane } = Tabs

const DEFAULT_MONITORING_CONFIG: MonitoringConfigType = {
  monitoringSampleRate: 0.1, // 10% sample rate as default
  blacklist: [],
}

export const MonitoringConfig = observer(() => {
  const { dataStore: { managingJsonStore } } = useStore()

  const [desktopForm] = Form.useForm<MonitoringConfigType>()
  const [iframeForm] = Form.useForm<MonitoringConfigType>()

  const [loading, setLoading] = useState(true)
  const [desktopConfig, setDesktopConfig] = useState<MonitoringConfigType>()
  const [iframeConfig, setIframeConfig] = useState<MonitoringConfigType>()

  useEffect(() => {
    (async() => {
      await managingJsonStore.getData()

      const desktopConfigData = managingJsonStore.itemTypes?.find(
        data => data.type === "monitoringConfig",
      )?.data as MonitoringConfigType | undefined

      const iframeConfigData = managingJsonStore.itemTypes?.find(
        data => data.type === "iframeMonitoringConfig",
      )?.data as MonitoringConfigType | undefined

      setDesktopConfig(desktopConfigData)
      setIframeConfig(iframeConfigData)

      const mergedDesktopConfig = { ...DEFAULT_MONITORING_CONFIG, ...desktopConfigData }
      const mergedIframeConfig = { ...DEFAULT_MONITORING_CONFIG, ...iframeConfigData }

      desktopForm.setFieldsValue(mergedDesktopConfig)
      iframeForm.setFieldsValue(mergedIframeConfig)

      setLoading(false)
    })()
  }, [managingJsonStore, desktopForm, iframeForm])

  const handleSaveAll = async() => {
    try {
      const [desktopVals, iframeVals] = await Promise.all([
        desktopForm.validateFields(),
        iframeForm.validateFields(),
      ])

      const upsert = (
        type: "monitoringConfig" | "iframeMonitoringConfig",
        oldConfig: MonitoringConfigType | undefined,
        formValues: MonitoringConfigType,
      ) => oldConfig
        ? managingJsonStore.update({ name: type, type, data: formValues })
        : managingJsonStore.create({ name: type, type, data: formValues })

      await Promise.all([
        upsert("monitoringConfig", desktopConfig, desktopVals),
        upsert("iframeMonitoringConfig", iframeConfig, iframeVals),
      ])

      notifications.success("Saved successfully")
      await managingJsonStore.getData()
    } catch (error) {
      console.error("Failed to save monitoring config:", error)
      notifications.error("Failed to save configuration")
    }
  }

  if (loading) return <Spin />

  return (
    <>
      <Tabs>
        <TabPane tab="Desktop Monitoring" key="desktop" forceRender>
          <Form form={desktopForm} layout="vertical">
            <MonitoringFields configType="monitoringConfig" />
          </Form>
        </TabPane>

        <TabPane tab="Iframe Monitoring" key="iframe" forceRender>
          <Form form={iframeForm} layout="vertical">
            <MonitoringFields configType="iframeMonitoringConfig" />
          </Form>
        </TabPane>
      </Tabs>

      <Button type="primary" onClick={handleSaveAll} style={{ marginBottom: 16 }}>
        Save All Tabs
      </Button>
    </>
  )
})
